import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Map, Home, FileText, HelpCircle, Mail, Sparkles } from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
  );
};

// Sitemap Section Component
const SitemapSection = ({ title, icon: Icon, links }) => (
  <div className="card p-6" style={{ background: "var(--background-200)" }}>
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--primary-100)" }}>
        <Icon size={20} style={{ color: "white" }} />
      </div>
      <h3 className="text-xl font-bold" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
        {title}
      </h3>
    </div>
    <ul className="space-y-3">
      {links.map((link, index) => (
        <li key={index}>
          <Link 
            to={link.href}
            className="flex items-center gap-2 text-sm hover:underline"
            style={{ color: "var(--text-200)" }}
          >
            <span style={{ color: "var(--primary-300)" }}>→</span>
            {link.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

// Main Sitemap Page Component
const SitemapPage = () => {
  const mainPages = [
    { href: "/", label: "Home" },
    { href: "/features", label: "Features" },
    { href: "/pricing", label: "Pricing" },
    { href: "/about", label: "About Us" },
    { href: "/used-equipment", label: "Used Equipment" },
  ];

  const productPages = [
    { href: "/how-it-works", label: "How It Works" },
    { href: "/3-steps", label: "The 3-Step Process" },
    { href: "/sales-workflow", label: "Sales Workflow" },
    { href: "/appraisal-to-market", label: "Appraisal to Market" },
    { href: "/valuation", label: "Valuation Process" },
    { href: "/marketing-distribution", label: "Marketing & Distribution" },
    { href: "/integrations", label: "Integrations" },
  ];

  const resourcePages = [
    { href: "/resources", label: "Resources" },
    { href: "/faq", label: "FAQ" },
    { href: "/getting-started", label: "Getting Started" },
    { href: "/why-choose-us", label: "Why Choose Us" },
  ];

  const legalPages = [
    { href: "/privacy", label: "Privacy Policy" },
    { href: "/terms", label: "Terms of Use" },
    { href: "/cookies", label: "Cookies Policy" },
    { href: "/security", label: "Security" },
  ];

  const supportPages = [
    { href: "/#contact", label: "Contact Us" },
    { href: "mailto:control@machinerymarketed.com", label: "Email Support" },
    { href: "tel:+61497085157", label: "Phone: +61 497 085 157" },
  ];

  const externalLinks = [
    { href: "https://mach-sales-hub.preview.emergentagent.com/login", label: "Sign In" },
    { href: "https://mach-sales-hub.preview.emergentagent.com/register", label: "Create Account" },
    { href: "https://www.instagram.com/machinery.marketed", label: "Instagram" },
    { href: "https://youtube.com/@machinerymarketed", label: "YouTube" },
    { href: "https://facebook.com/machinerymarketed", label: "Facebook" },
  ];

  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative py-20">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://www.machinerymarketed.com/hero_excavator.jpg" 
              alt="Construction machinery"
              className="w-full h-full object-cover"
              style={{ opacity: 0.2 }}
              loading="eager"
            />
            <div 
              className="absolute inset-0"
              style={{ background: "linear-gradient(to bottom, rgba(0,12,37,0.8) 0%, var(--background-100) 100%)" }}
            />
          </div>
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6"
                style={{ background: "rgba(15, 95, 255, 0.2)", border: "1px solid rgba(15, 95, 255, 0.3)" }}
              >
                <Sparkles size={18} style={{ color: "var(--primary-300)" }} />
                <span style={{ color: "var(--text-100)" }}>Site Navigation</span>
              </div>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                Site <span className="text-gradient">Map</span>
              </h1>
              <p className="text-xl max-w-3xl mx-auto" style={{ color: "var(--text-200)" }}>
                Find your way around MachineryMarketed. All our pages organized for easy navigation.
              </p>
            </div>
          </div>
        </section>

        {/* Sitemap Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <SitemapSection 
                title="Main Pages" 
                icon={Home} 
                links={mainPages}
              />
              <SitemapSection 
                title="Product" 
                icon={Map} 
                links={productPages}
              />
              <SitemapSection 
                title="Resources" 
                icon={FileText} 
                links={resourcePages}
              />
              <SitemapSection 
                title="Legal" 
                icon={FileText} 
                links={legalPages}
              />
              <SitemapSection 
                title="Support" 
                icon={HelpCircle} 
                links={supportPages}
              />
              <SitemapSection 
                title="External Links" 
                icon={Mail} 
                links={externalLinks}
              />
            </div>
          </div>
        </section>

        {/* XML Sitemap Link */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                XML Sitemap
              </h2>
              <p className="mb-8" style={{ color: "var(--text-200)" }}>
                For search engines and technical purposes, we also provide an XML sitemap.
              </p>
              <a 
                href="/sitemap.xml"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2"
              >
                View XML Sitemap
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/" className="footer-link text-sm">Home</Link>
              <Link to="/features" className="footer-link text-sm">Features</Link>
              <Link to="/about" className="footer-link text-sm">About</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SitemapPage;
